import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axiosInstance from '../../api/axiosConfig'; // Adjust the path as needed
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  useTheme,
  Skeleton, // Import Skeleton for loading state
  Fade // Import Fade for smooth transitions
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const logger = {
  info: (...args) => console.log('[INFO]', ...args),
  error: (...args) => console.error('[ERROR]', ...args),
};

// Helper to format currency (adjust as needed)
const formatCurrency = (amount) => {
    // Basic formatting, consider locale and currency symbol later
    if (amount === null || amount === undefined || isNaN(amount)) {
        return 'N/A'; // Handle null or invalid numbers
    }
    return amount.toFixed(2);
};

function CurrentBalanceDisplay({ schoolId }) {
  const theme = useTheme();
  const [balanceData, setBalanceData] = useState(null); // Store the whole balance object if needed, or just the value
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBalance = useCallback(async () => {
    // Ensure schoolId is valid before fetching
    if (!schoolId || schoolId <= 0) {
      logger.info('CurrentBalanceDisplay: Invalid or missing schoolId, skipping fetch.');
      // Reset state if schoolId becomes invalid after being valid
      setLoading(false);
      setError(null); // No error, just no data to fetch
      setBalanceData(null); // Clear balance
      return;
    }

    setLoading(true);
    setError(null);
    logger.info(`CurrentBalanceDisplay: Fetching balance for schoolId: ${schoolId}`);

    try {
      // Endpoint uses the schoolId from the header (set by axiosInstance)
      const response = await axiosInstance.get(`/api/account-balance/current/${schoolId}`);

      if (response.data && typeof response.data.balance !== 'undefined') {
        setBalanceData(response.data); // Store the response data
        logger.info(`CurrentBalanceDisplay: Fetched balance: ${response.data.balance}`);
      } else {
         logger.error('CurrentBalanceDisplay: Invalid balance data received:', response.data);
         // Handle cases where balance might not exist yet for a school
         setBalanceData({ schoolId: schoolId, balance: 0.0 }); // Assume 0 if not found/invalid? Or show error?
         // setError('Balance data not found for this school.'); // Alternative: show error
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || 'Failed to fetch current balance.';
      setError(errMsg);
      setBalanceData(null); // Clear data on error
      logger.error('CurrentBalanceDisplay: Error fetching balance:', errMsg, err.response || err);
    } finally {
      setLoading(false);
    }
  }, [schoolId]); // Dependency is schoolId

  // useEffect to trigger fetch when schoolId changes (or component mounts)
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]); // fetchBalance is memoized and changes only when schoolId changes

  // --- Render Logic ---

  const renderContent = () => {
    // Loading State using Skeleton - Mimics the final layout
    if (loading) {
      return (
         <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" height={20}/>
              <Skeleton variant="text" width="80%" height={30}/>
            </Box>
         </Stack>
      );
    }

    // Error State
    if (error) {
      return (
        <Fade in={!!error}>
            <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit"/>} sx={{ width: '100%' }}>
                {error}
            </Alert>
        </Fade>
      );
    }

    // Success/Data State
    const currentBalance = balanceData?.balance ?? null; // Use nullish coalescing, default to null if data is missing
    const balanceValueExists = currentBalance !== null;

     // Determine color based on balance
    const balanceColor = balanceValueExists && currentBalance > 0 ? theme.palette.success.dark
                       : balanceValueExists && currentBalance === 0 ? theme.palette.warning.dark
                       : theme.palette.error.dark; // Use error for negative or N/A (though credits likely aren't negative)

    return (
       <Fade in={!loading && !error}>
            <Stack direction="row" spacing={2} alignItems="center">
                <AccountBalanceWalletIcon sx={{ fontSize: 40, color: balanceColor }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                        Current Account Balance
                    </Typography>
                    <Typography
                        variant="h5"
                        component="p"
                        fontWeight="bold"
                        color={balanceColor} // Dynamic color
                        sx={{ lineHeight: 1.3 }}
                    >
                        {formatCurrency(currentBalance)} Credits
                    </Typography>
                </Box>
            </Stack>
       </Fade>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2.5, // Slightly more padding
        borderRadius: 2,
        // Optional: Add a subtle border or background
        // border: `1px solid ${theme.palette.divider}`
        // backgroundColor: alpha(theme.palette.primary.light, 0.05)
      }}
    >
        {renderContent()}
    </Paper>
  );
}

CurrentBalanceDisplay.propTypes = {
  /** The ID of the school whose balance should be displayed. */
  schoolId: PropTypes.number.isRequired,
};

export default CurrentBalanceDisplay;